import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {CdkDragDrop, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {GroupService} from '../../../Services/groups.service';
import {GroupModel} from '../../../Models/group-model';
import {ClientService} from '../../../../clients-module/services/client.service';
import {ClientLocation} from '../../../../clients-module/models/clientLocation';
import {PropertyService} from '../../../../property-module/services/property.service';
import {LocationListingType} from '../../../../shared-components-module/drag-drop-location/locationListingType';

@Component({
  selector: 'app-group-setup',
  templateUrl: './group-setup.component.html',
  styleUrls: ['./group-setup.component.less'],
  encapsulation: ViewEncapsulation.None,
})
export class GroupSetupComponent implements OnInit {

  public form: UntypedFormGroup;
  public locationFilter = new UntypedFormControl('');

  public locations: ClientLocation[] = [];
  public availableLocations: ClientLocation[] = [];
  public selectedLocations: ClientLocation[] = [];

  public selectedGroup: GroupModel;

  public step = 0;
  public type = PageType.Modify;

  constructor(
    public readonly groupService: GroupService,
    private readonly dialog: MatDialog,
    public matDialogRef: MatDialogRef<GroupSetupComponent>,
    private readonly clientService: ClientService,
    private readonly propertyService: PropertyService,
    private readonly formBuilder: UntypedFormBuilder) {

  }

  public ngOnInit(): void {
    this.createForm();

    this.propertyService.propertiesLoaded.subscribe(() => {
      this.locations = this.propertyService.properties;
      this.availableLocations = this.propertyService.properties;
    });

    this.propertyService.FetchPropertiesForClient(this.clientService.currentClient.id);

    this.registerLocationFilterSubscriber();
  }

  public registerLocationFilterSubscriber(): void {
    this.locationFilter.valueChanges.subscribe((name: string) => {

      if (name) {
        const tempList = this.locations.filter((w: ClientLocation) => w.locationName.toLowerCase().indexOf(name) !== -1);

        this.selectedLocations.forEach((r: ClientLocation) => {
          const idx = tempList.findIndex((l: ClientLocation) => l.id === r.id);

          if (idx > -1) {
            tempList.splice(idx, 1);
          }
        });

        this.availableLocations = tempList;

      } else {
        // filter was cleared, so put the available list back to total list minus the selected list

        const tempList = this.locations;

        this.selectedLocations.forEach((r: ClientLocation) => {
          const idx = tempList.findIndex((l: ClientLocation) => l.id === r.id);

          if (idx > -1) {
            tempList.splice(idx, 1);
          }
        });

        this.availableLocations = tempList;
      }
    });
  }

  public createForm(): void {
    this.form = this.formBuilder.group({
      id: [0, Validators.required],
      clientId: [this.clientService.currentClient.id, Validators.required],
      groupName: ['', Validators.required]
    });
  }

  public drop(event: CdkDragDrop<string[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex);
      this.ResortArrays();
    }
  }

  public next(): void {

    if (this.step === 0) {
      this.step = 1;

      if (this.type === PageType.Modify) {

        // Populate the selected list with the group ID's and remove those ids from the available list
        this.form.patchValue(this.selectedGroup);

        this.selectedGroup.selectedLocations.forEach((r: number) => {

          const tt = this.availableLocations.find((e: ClientLocation) => e.id === r);

          this.selectedLocations.push(tt);

          const idx = this.availableLocations.findIndex((l: ClientLocation) => l.id === r);

          if (idx > -1) {
            this.availableLocations.splice(idx, 1);
          }
        });
      }

      return;
    }

    // Here is where we will save the information
    const selectedIds = this.selectedLocations.map((e: ClientLocation) => e.id);

    const t: GroupModel = this.form.value;

    t.selectedLocations = selectedIds;

    if (this.type === PageType.Add) {

      this.groupService.saveNewGroup(t).subscribe(() => {
        this.matDialogRef.close();
      });
    } else {
      this.groupService.updateGroup(t).subscribe(() => {
        this.matDialogRef.close();
      });
    }
  }

  public cancel(): void {
    // TODO Perhaps add a confirm dialog here?
    this.matDialogRef.close();
  }

  public addRemovedClicked(e: LocationListingType, index: number): void {

    if (e === LocationListingType.Add) {
      this.selectedLocations.push(this.availableLocations[index]);
      this.availableLocations.splice(index, 1);
    }

    if (e === LocationListingType.Remove) {
      this.availableLocations.push(this.selectedLocations[index]);
      this.selectedLocations.splice(index, 1);
    }

    this.ResortArrays();
  }

  public ResortArrays(): void {
    this.selectedLocations.sort((a, b) => a.locationName.localeCompare(b.locationName));
    this.availableLocations.sort((a, b) => a.locationName.localeCompare(b.locationName));
  }

  public addAll(): void {

    if (this.availableLocations.length > 0) {
      for (let x = 0; x < this.availableLocations.length; x++) {
        this.selectedLocations.push(this.availableLocations[x]);
      }

      this.availableLocations = [];

      this.ResortArrays();
    }
  }

  public removeAll(): void {
    if (this.selectedLocations.length > 0) {
      for (let x = 0; x < this.selectedLocations.length; x++) {
        this.availableLocations.push(this.selectedLocations[x]);
      }

      this.selectedLocations = [];

      this.ResortArrays();
    }
  }

  public createNewGroup(): void {
    this.type = PageType.Add;
  }

}

export enum PageType {
  Modify,
  Add = 1
}

